'use client'

import {useCallback, useEffect, useMemo, useState} from 'react'
import {apiGet} from '@/lib/api'
import {avatarFor} from '@/lib/utils'

export type AccountStatus = 'online' | 'offline' | 'expired' | 'logged_out'

export type LoginStatusInfo = {
    loginState: number
    loginErrMsg: string
    onlineTime: string
    loginTime: string
    onlineDays: number
    expiryTime: string
}

export type AccountItem = {
    wxid: string
    nickName: string
    avatar: string
    protocol: string
    proxyUrl: string
    status: AccountStatus
    loginInfo?: LoginStatusInfo
}

type ApiAccount = {
    wxid: string
    nickName: string
    avatar: string
    protocol: string
    proxyUrl: string
    status: string
}

type AccountStatusEvent = {
    type: 'account_status'
    wxid: string
    status: AccountStatus
    loginInfo?: LoginStatusInfo
}

export function useAccounts(currentWxid?: string) {
    const [accounts, setAccounts] = useState<AccountItem[]>([])
    const [loading, setLoading] = useState(true)

    const refreshAccounts = useCallback(async () => {
        setLoading(true)
        try {
            const res = await apiGet<{ success: boolean; accounts: ApiAccount[] }>('/accounts?page=1&pageSize=100', {silent: true})
            if (res.success && Array.isArray(res.accounts)) {
                setAccounts(res.accounts.map((a) => ({
                    wxid: a.wxid,
                    nickName: a.nickName || a.wxid,
                    avatar: a.avatar || avatarFor(a.wxid),
                    protocol: a.protocol || '',
                    proxyUrl: a.proxyUrl || '',
                    status: (a.status || 'offline') as AccountStatus,
                })))
            }
        } catch {
            // silent
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        refreshAccounts()
    }, [refreshAccounts, currentWxid])

    const accountWxids = useMemo(
        () => accounts.map((account) => account.wxid).filter(Boolean).sort(),
        [accounts],
    )
    const accountWxidsKey = accountWxids.join(',')

    useEffect(() => {
        const accountWxids = accountWxidsKey ? accountWxidsKey.split(',') : []
        if (!accountWxids.length) return
        const sources = accountWxids.map((wxid) => {
            const source = new EventSource('/api/accounts/events?wxid=' + encodeURIComponent(wxid))
            const onStatus = (event: MessageEvent) => {
                let payload: AccountStatusEvent
                try {
                    payload = JSON.parse(event.data) as AccountStatusEvent
                } catch {
                    return
                }
                if (payload.type !== 'account_status' || payload.wxid !== wxid || !payload.status) return
                setAccounts((current) => current.map((account) => (
                    account.wxid === wxid
                        ? {...account, status: payload.status, loginInfo: payload.loginInfo}
                        : account
                )))
            }
            source.addEventListener('account_status', onStatus as EventListener)
            return {source, onStatus}
        })
        return () => {
            for (const {source, onStatus} of sources) {
                source.removeEventListener('account_status', onStatus as EventListener)
                source.close()
            }
        }
    }, [accountWxidsKey])

    return {accounts, loading, refreshAccounts}
}
